import type { SignageItem } from "@shared/types/signage";
import type { ContentPlayEvent } from "@shared/types/contentPlay";
import { adIdOfItem } from "./vistarAds";

/**
 * Proof-of-play for Foyer's own signage player (`ANALYTICS-4`).
 *
 * One `content_start` of kind `signage` per item that actually came on
 * screen, over the existing engagement channel into `content_play_daily`
 * (premise × day × kind × key). Analytics → Content analytics → "Most watched"
 * → Signage reads it beside the autoplayer's plays.
 *
 * The KEY names the MATERIAL, not the playlist slot: an item id is only unique
 * inside its playlist, while "how often was the breakfast promo shown" is
 * asked across every playlist and break that carries it. So:
 *
 *   catalogue title  → `content:<contentAssetId>`
 *   template         → `template:<templateInstanceId>`
 *   web page         → `web:<origin+path>` (no query: tokens live there)
 *   file             → `file:<assetUrl>` (the stable `/foyer-assets/…` URL)
 *
 * Nothing about the screen or the room is sent; the premise comes from the
 * screen token server-side, exactly as for every other play report.
 *
 * A Vistar ad is NOT reported here. It has its own, stricter proof of play
 * (`POST .../ad/registerview`, counted only when the ad ran to its end and
 * Vistar accepted it), and counting its start here as well would put every
 * ad into "Most shown signage" a second time, under its CDN URL.
 */
export function signagePlayKey(item: SignageItem): string | null {
  if (item.contentAssetId) return `content:${item.contentAssetId}`;
  if (item.templateInstanceId) return `template:${item.templateInstanceId}`;
  if (item.webUrl) {
    try {
      const u = new URL(item.webUrl);
      return `web:${u.origin}${u.pathname}`;
    } catch {
      return null;
    }
  }
  if (item.assetUrl) return `file:${item.assetUrl}`;
  return null;
}

/** A human label: the file name, the page, or the playlist as a fallback. */
export function signagePlayLabel(
  item: SignageItem,
  playlistName?: string
): string {
  if (item.webUrl) {
    try {
      const u = new URL(item.webUrl);
      return `${u.hostname}${u.pathname === "/" ? "" : u.pathname}`;
    } catch {
      /* fall through */
    }
  }
  const file = item.assetUrl?.split("/").pop();
  if (file) {
    try {
      return decodeURIComponent(file);
    } catch {
      return file;
    }
  }
  return playlistName || item.id;
}

/**
 * The play report for `item`, or null when it is not reported here.
 *
 * `onBreak` says where the item came from: a campaign break (`signage`, what
 * "Most shown signage" lists) or the screen's own background loop
 * (`signage_loop`: the customer's own material, recorded but never listed as
 * a campaign).
 */
export function signagePlayEvent(
  item: SignageItem,
  playlistName?: string,
  opts: { onBreak?: boolean } = {}
): ContentPlayEvent | null {
  if (adIdOfItem(item.id)) return null;
  const key = signagePlayKey(item);
  if (!key) return null;
  return {
    type: "content_start",
    kind: opts.onBreak ? "signage" : "signage_loop",
    assetId: key.slice(0, 500),
    label: signagePlayLabel(item, playlistName).slice(0, 200),
  };
}
